/**
 * Cross-platform secure storage utility.
 * Uses expo-secure-store on native platforms (iOS/Android)
 * and falls back to localStorage on web.
 *
 * Note: localStorage is NOT as secure as SecureStore on native.
 * For web, consider using httpOnly cookies for sensitive tokens in production.
 */
import { Platform } from "react-native";
import * as SecureStore from "expo-secure-store";

// Check if we're on web
const isWeb = Platform.OS === "web";

/**
 * Store a value securely
 */
export async function setSecureItem(key: string, value: string): Promise<void> {
  if (isWeb) {
    try {
      localStorage.setItem(key, value);
    } catch (error) {
      throw error;
    }
  } else {
    await SecureStore.setItemAsync(key, value);
  }
}

/**
 * Retrieve a value from secure storage
 */
export async function getSecureItem(key: string): Promise<string | null> {
  if (isWeb) {
    try {
      return localStorage.getItem(key);
    } catch (error) {
      return null;
    }
  } else {
    return await SecureStore.getItemAsync(key);
  }
}

/**
 * Delete a value from secure storage
 */
export async function deleteSecureItem(key: string): Promise<void> {
  if (isWeb) {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      throw error;
    }
  } else {
    await SecureStore.deleteItemAsync(key);
  }
}

// Export as a namespace-like object for drop-in replacement
export const secureStorage = {
  setItemAsync: setSecureItem,
  getItemAsync: getSecureItem,
  deleteItemAsync: deleteSecureItem,
};

export default secureStorage;
